"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useLocalizedRouter } from "@/lib/localized-router";
import { locales } from "@/lib/i18n-config";
import { isNativeApp, openExternal } from "@/lib/native-app";
import { captureEvent } from "@/lib/tracking";
import { getClientId } from "@/lib/client-id";

const LOCALE_RE = new RegExp(`^/(${locales.join("|")})(?=/|$)`);

export interface User {
  id: string;
  email: string | null;
  name: string | null;
  picture_url: string | null;
  provider: string;
  tier: string;
  email_verified?: boolean;
  /** New-account Pro preview: days remaining (feature gates run at
   *  "paid") or how many days ago it expired (≤14, honest-loss copy). */
  pro_preview_days_left?: number | null;
  pro_preview_ended_days_ago?: number | null;
  /** Where the preview days come from: the opt-in week ("trial") or a
   *  granted month ("bonus", the feedback-for-Pro reward). A bonus has
   *  no "end the trial" control and a month-long countdown bar. */
  pro_preview_kind?: "trial" | "bonus" | null;
  /** The whole length the countdown runs against (7 for the week, 30 or
   *  more for a bonus), for the progress bar. */
  pro_preview_total_days?: number | null;
  /** Free user who has never started a trial - show the offer card. */
  pro_trial_eligible?: boolean;
  /** Account creation timestamp (ISO). Drives the profile
   *  "member since" line; null on legacy rows. */
  created_at?: string | null;
  /** Trial mechanic resolved for THIS user: "card" (register a card
   *  upfront, auto-converts on day 7) or "nocard" (one-tap trial that
   *  auto-downgrades). Copy must always match the backend's answer. */
  trial_mode?: "card" | "nocard";
  /** OTP-verified contact phone (set once, immutable) - first-party
   *  listings publish it as the contact number. */
  phone?: string | null;
  /** Public account flavor: personal | agent | developer. */
  account_type?: string;
  /** What the person is here for: buy | rent | sell | invest (null until
   *  chosen on the homepage). Orders the signed-in homepage. */
  goal?: string | null;
}

/** GET /auth/tokens. The name survives from the token era: since
 *  2026-10-18 any account is unlimited (balance -1) and the only number
 *  left is an anonymous device's free analyses before sign-in. Kept as
 *  `tokens` on the context so the many `tokens?.is_subscriber` Pro
 *  checks did not have to move. */
interface TokenBalance {
  balance: number; // -1 = unlimited (any signed-in account); else free analyses left on this device
  tier: "anonymous" | "free" | "paid";
  is_subscriber: boolean;
  /** Anonymous caller on a device that signed in before: no anonymous
   *  quota, the UI should say "you already have an account". */
  account_required?: boolean;
  /** Masked address of that account ("h•••@mail.ru"), when known. */
  email_hint?: string | null;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /** Trial mechanic for the CURRENT viewer - resolved by the backend
   *  and present even when anonymous (pre-signup copy honesty). */
  trialMode: "card" | "nocard";
  tokens: TokenBalance | null;
  refreshTokens: () => Promise<void>;
  signIn: () => void;
  signInWithGoogle: () => void;
  signInWithFacebook: () => void;
  loginWithEmail: (email: string, password: string, turnstileToken?: string | null) => Promise<{ error?: string }>;
  registerWithEmail: (email: string, password: string, name?: string, turnstileToken?: string | null, locale?: string, referralCode?: string | null) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  trialMode: "nocard",
  tokens: null,
  refreshTokens: async () => {},
  signIn: () => {},
  signInWithGoogle: () => {},
  signInWithFacebook: () => {},
  loginWithEmail: async () => ({}),
  registerWithEmail: async () => ({}),
  signOut: async () => {},
  setUser: () => {},
});

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";


/** Random RFC 4122 v4 UUID with a getRandomValues fallback.
 *
 * crypto.randomUUID only exists in **secure contexts** (HTTPS or
 * localhost). Mobile Safari on iOS 18 throws "crypto.randomUUID is
 * not a function" when the page was loaded over plain http:// -
 * happens when a user opens an http link we redirected from, tabs
 * back to the cached http version, or is behind a middlebox that
 * re-serves our TLS. The polyfill uses getRandomValues (available
 * in every environment with a WebCrypto implementation, secure or
 * not) and formats the bytes as a v4 UUID per RFC 4122.
 *
 * Seen in Sentry: MENZIL-WEB-7.
 */
function randomUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [trialMode, setTrialMode] = useState<"card" | "nocard">("nocard");
  const [tokens, setTokens] = useState<TokenBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useLocalizedRouter();

  const refreshTokens = useCallback(async () => {
    try {
      const fp = localStorage.getItem("bina-fp") || "";
      // Same identity the gate spends: the client id first, the
      // fingerprint for older clients. Without the client id the header
      // read a different bucket than the one /analyze charged.
      const res = await fetch(`${API_BASE}/auth/tokens`, {
        credentials: "include",
        headers: { "X-Fingerprint": fp, "X-Client-Id": getClientId() },
      });
      if (res.ok) {
        setTokens(await res.json());
      }
    } catch {
      // silent - tokens badge just won't update
    }
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/auth/me`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.trial_mode === "card" || data.trial_mode === "nocard") {
          setTrialMode(data.trial_mode);
        }
        if (data.authenticated && data.user) {
          setUser(data.user);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        refreshTokens();
      });
  }, [refreshTokens]);

  const signIn = useCallback(() => {
    // Build a return_to from the current page so the user lands back
    // where they were after signing in. Guard for SSR (window is
    // undefined) and for when the user is already on an /auth route
    // (to avoid a redirect loop).
    if (typeof window !== "undefined") {
      const localeLessPath = window.location.pathname.replace(LOCALE_RE, "") || "/";
      const isAuthRoute = localeLessPath.startsWith("/auth");
      if (!isAuthRoute) {
        const returnTo = localeLessPath + window.location.search;
        router.push(`/auth/login?return_to=${encodeURIComponent(returnTo)}`);
        return;
      }
    }
    router.push("/auth/login");
  }, [router]);

  const signInWithGoogle = useCallback(() => {
    const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
    if (!clientId) {
      alert("Google OAuth is not configured yet.");
      return;
    }
    // Mobile shell: Google refuses OAuth in WebViews, so the flow runs
    // in the system browser via /auth/app-login and returns through the
    // az.menzil.app:// deep link (AppShellBridge exchanges the one-time
    // code for this WebView's own session cookie). return_to travels by
    // URL because the system browser can't see our sessionStorage.
    if (isNativeApp()) {
      const returnTo = sessionStorage.getItem("oauth_return_to") ?? "/";
      sessionStorage.removeItem("oauth_return_to");
      void openExternal(
        `${window.location.origin}/auth/app-login?provider=google` +
        `&return_to=${encodeURIComponent(returnTo)}`,
      );
      return;
    }
    // PWA caveat: this does a full-page redirect to accounts.google.com.
    // Inside an installed PWA (display: standalone), Google sometimes
    // classifies the navigation as an embedded-webview request and
    // returns "This browser or app may not be secure". The manifest
    // pins `display_override: ["minimal-ui"]` so the PWA exposes a
    // small browser strip during cross-origin navigations, which
    // satisfies Google's UA check. If the block returns, prefer the
    // GoogleOneTap component (iframe postMessage) over this redirect.
    // Generate cryptographic random state to prevent CSRF attacks.
    // Format: "google:<random>" - the provider prefix is checked on callback,
    // the random part is verified against sessionStorage.
    const nonce = randomUUID();
    const state = `google:${nonce}`;
    sessionStorage.setItem("oauth_state", state);

    const redirectUri = `${window.location.origin}/auth/callback`;
    const url =
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent("openid email profile")}` +
      `&access_type=offline` +
      `&prompt=consent` +
      `&state=${encodeURIComponent(state)}`;
    window.location.href = url;
  }, []);

  const signInWithFacebook = useCallback(() => {
    const appId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID;
    if (!appId) {
      alert("Facebook OAuth is not configured yet.");
      return;
    }
    // Same system-browser detour as signInWithGoogle - see the comment
    // there. Facebook's WebView stance is looser, but one path is one
    // path.
    if (isNativeApp()) {
      const returnTo = sessionStorage.getItem("oauth_return_to") ?? "/";
      sessionStorage.removeItem("oauth_return_to");
      void openExternal(
        `${window.location.origin}/auth/app-login?provider=facebook` +
        `&return_to=${encodeURIComponent(returnTo)}`,
      );
      return;
    }
    const nonce = randomUUID();
    const state = `facebook:${nonce}`;
    sessionStorage.setItem("oauth_state", state);

    const redirectUri = `${window.location.origin}/auth/callback`;
    const url =
      `https://www.facebook.com/v19.0/dialog/oauth?` +
      `client_id=${appId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent("email,public_profile")}` +
      `&response_type=code` +
      `&state=${encodeURIComponent(state)}`;
    window.location.href = url;
  }, []);

  const loginWithEmail = useCallback(async (email: string, password: string, turnstileToken?: string | null) => {
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, turnstile_token: turnstileToken || undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { error: data.detail || "Login failed" };
      }
      const data = await res.json();
      setUser(data.user);
      await refreshTokens();
      captureEvent("user_logged_in", { provider: "email" });
      return {};
    } catch {
      return { error: "Network error" };
    }
  }, [refreshTokens]);

  const registerWithEmail = useCallback(async (email: string, password: string, name?: string, turnstileToken?: string | null, locale?: string, referralCode?: string | null) => {
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, name: name || undefined, turnstile_token: turnstileToken || undefined, locale, referral_code: referralCode || undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { error: data.detail || "Registration failed" };
      }
      const data = await res.json();
      setUser(data.user);
      await refreshTokens();
      captureEvent("user_signed_up", { provider: "email", has_referral: !!referralCode });
      return {};
    } catch {
      return { error: "Network error" };
    }
  }, [refreshTokens]);

  const signOut = useCallback(async () => {
    await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    setUser(null);
  }, []);

  // Memoized: the provider sits above every page, so a fresh object per
  // render re-rendered every useAuth() consumer on each parent render.
  const value = useMemo(() => ({
    user, loading, trialMode, tokens, refreshTokens, signIn, signInWithGoogle, signInWithFacebook,
    loginWithEmail, registerWithEmail, signOut, setUser,
  }), [user, loading, trialMode, tokens, refreshTokens, signIn, signInWithGoogle, signInWithFacebook,
    loginWithEmail, registerWithEmail, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
